import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { AppState } from "../data/reducers";
import { countPassengers } from "../lib/functions";
import {
  PassengerTypes,
  PassengersCount,
} from "../lib/golObjectTypes/PassengerTypes";
import periodCalendarV2, { FlightType } from "../lpc/api/periodCalendarV2";
import { FlightDirection } from "../types";

const MAX_PASSENGERS_COUNT = 9;
const MAX_CHECK_MONTHS = 12;

interface ILowCostSearch {
  enabled: boolean;
  passengers: PassengersCount;
  addPassenger: (types: PassengerTypes) => void;
  removePassenger: (type: PassengerTypes) => void;
  flightsLoaded: boolean;
  getFirstDayOfMonthOffset: (month: number) => string;
  getFlights: (
    origin: string,
    destination: string,
    month: number
  ) => {
    departure: string;
    price: number;
    totalPrice: number;
    currency: string;
    isLowestPrice: boolean;
  }[];
  selectDay: (day: Date, flightDirection: FlightDirection) => void;
  selectedDays: { from: Date; to: Date };
  getSelectedFlightsTotalPrice: () => { totalPrice: number; currency: string };
  firstAvailableMonthOffset: number | undefined;
}

const getFirstDayOfMonthOffset = (month: number) =>
  moment().add(month, "month").startOf("month").format("YYYY-MM-DD");

const getLastDayOfMonthOffset = (month: number) =>
  moment().add(month, "month").endOf("month").format("YYYY-MM-DD");

const getFirstAvailableMonthOffsetAndDateFromFlights = (
  flights: any[],
  origin: string
): {
  monthIndex: number | undefined;
  availableDate: string | undefined;
} => {
  const today = moment().format("YYYY-MM-DD");
  const filterByDeparture = (flight) => flight.requestParams?.origin === origin;
  const sortByMonthIndex = (a, b) => a.monthIndex - b.monthIndex;
  const findFirstAvailableMonthOffset = (acc, result: any) => {
    if (acc.found) {
      return acc;
    }

    const { response, monthIndex } = result;
    const flightDates = Object.keys(response || {}).sort();

    const hasAvailableFlights = flightDates.some((date) =>
      moment(date).isSameOrAfter(today)
    );

    if (hasAvailableFlights) {
      const availableDate = flightDates.find((date) =>
        moment(date).isSameOrAfter(today)
      );
      return { found: true, monthIndex, availableDate };
    }

    return acc;
  };

  const res = flights
    .filter(filterByDeparture)
    .sort(sortByMonthIndex)
    .reduce(findFirstAvailableMonthOffset, {
      found: false,
      monthIndex: undefined,
      availableDate: undefined,
    });
  return res;
};

const isSameDay = (day1: Date, day2: Date) => moment(day1).isSame(day2, "day");

export function useLowCostSearch(query: {
  origin: string;
  destination: string;
}): ILowCostSearch {
  const [passengers, setPassengers] = useState({
    ADT: 1,
    INF: 0,
    CHD: 0,
    YTH: 0,
    YCD: 0,
  });

  const passengerAmount = countPassengers(passengers);
  const [flightsLoaded, setFlightsLoaded] = useState(false);
  const [periodCalendarResults, setPeriodCalendarResults] = useState([]);
  const [firstAvailableMonthOffset, setFirstAvailableMonthOffset] = useState(
    undefined
  );
  const [firstAvailableFlightDate, setFirstAvailableFlightDate] = useState(
    undefined
  );
  const [selectedDays, setSelectedDays] = useState({ from: null, to: null });

  const lowCostCalendarsPageEnabled = useSelector(
    (state: AppState) =>
      state.storage?.frontendSettings?.dealerFrontendSettings
        ?.low_cost_calendars_page_enabled === "true"
  );
  const lowCostCalendarsPageApiUrl = useSelector(
    (state: AppState) =>
      state.storage?.frontendSettings?.dealerFrontendSettings
        ?.low_cost_calendars_page_api_url
  );
  const lowCostCalendarsPageApiToken = useSelector(
    (state: AppState) =>
      state.storage?.frontendSettings?.dealerFrontendSettings
        ?.low_cost_calendars_page_api_token
  );

  useEffect(() => {
    const promises = [
      { origin: query.origin, destination: query.destination },
      { origin: query.destination, destination: query.origin },
    ]
      .map(({ origin, destination }) =>
        Array.from({ length: MAX_CHECK_MONTHS }).map((u, i) => {
          const requestParams = {
            origin,
            destination,
            periodStart: getFirstDayOfMonthOffset(i),
            periodEnd: getLastDayOfMonthOffset(i),
            flightType: FlightType.ONEWAY,
          };

          if (
            !lowCostCalendarsPageEnabled ||
            !lowCostCalendarsPageApiUrl ||
            !lowCostCalendarsPageApiToken
          ) {
            return Promise.resolve({
              response: {},
              requestParams,
            });
          }

          return new Promise((resolve) => {
            periodCalendarV2(requestParams, {
              lpcApiUrl: lowCostCalendarsPageApiUrl,
              lpcToken: lowCostCalendarsPageApiToken,
            }).then((response) => {
              resolve({
                response: Object.keys(response).reduce((acc, key) => {
                  const flight = response[key][0];
                  const [price, currency] = flight.price.split(" ");
                  return {
                    ...acc,
                    [key]: [
                      {
                        ...flight,
                        price,
                        currency,
                      },
                    ],
                  };
                }, {}),
                requestParams,
                monthIndex: i,
              });
            });
          });
        })
      )
      .reduce((acc, curr) => [...acc, ...curr], []);

    Promise.all(promises).then((responses) => {
      setPeriodCalendarResults(responses);
      setFlightsLoaded(true);

      const {
        monthIndex: firstMonthOffset,
        availableDate: firstAvailableDate,
      } = getFirstAvailableMonthOffsetAndDateFromFlights(
        responses,
        query.origin
      );

      setFirstAvailableMonthOffset(firstMonthOffset);
      setFirstAvailableFlightDate(firstAvailableDate);
    });
  }, [
    lowCostCalendarsPageEnabled,
    lowCostCalendarsPageApiUrl,
    lowCostCalendarsPageApiToken,
    query.origin,
    query.destination,
  ]);

  const addPassenger = useCallback(
    (type: PassengerTypes) => {
      if (countPassengers(passengers) >= MAX_PASSENGERS_COUNT) {
        return;
      }
      setPassengers({
        ...passengers,
        [type]: passengers[type] + 1,
      });
    },
    [passengers]
  );

  const removePassenger = useCallback(
    (type: PassengerTypes) => {
      if (passengers[type] < 1) {
        return;
      }
      setPassengers({
        ...passengers,
        [type]: passengers[type] - 1,
      });
    },
    [passengers]
  );

  const getFlights = (
    origin: string,
    destination: string,
    month: number
  ): {
    departure: string;
    price: number;
    totalPrice: number;
    currency: string;
    isLowestPrice: boolean;
  }[] => {
    const periodStart = getFirstDayOfMonthOffset(month);
    const foundResult = periodCalendarResults.find((result) => {
      return (
        result.requestParams.origin === origin &&
        result.requestParams.destination === destination &&
        result.requestParams.periodStart === periodStart
      );
    });

    if (!foundResult) {
      return [];
    }

    const { response } = foundResult;

    const flights = Object.keys(response)
      .map((key) => {
        return response[key][0];
      })
      .filter(
        (flight) =>
          moment(flight.departure).isSameOrAfter(
            moment().format("YYYY-MM-DD")
          ) &&
          moment(flight.departure).isSameOrAfter(
            moment(firstAvailableFlightDate).format("YYYY-MM-DD")
          )
      );

    const lowestPrice = flights.reduce<number>((acc, curr) => {
      if (!acc) {
        return curr.price;
      }

      return Number(curr.price) < Number(acc) ? curr.price : acc;
    }, null);

    return flights.map((flight) => {
      return {
        ...flight,
        totalPrice: flight.price * passengerAmount,
        isLowestPrice: flight.price === lowestPrice,
      };
    });
  };

  const selectDay = useCallback(
    (day: Date, flightDirection: FlightDirection) => {
      if (flightDirection === "return") {
        if (selectedDays.to && isSameDay(day, selectedDays.to)) {
          setSelectedDays({ ...selectedDays, to: null });
        } else if (
          selectedDays.from &&
          moment(day).isBefore(moment(selectedDays.from))
        ) {
          setSelectedDays({ from: null, to: day });
        } else {
          setSelectedDays({ ...selectedDays, to: day });
        }
        return;
      }

      if (selectedDays.from && isSameDay(day, selectedDays.from)) {
        setSelectedDays({ ...selectedDays, from: null });
      } else if (
        selectedDays.to &&
        moment(day).isAfter(moment(selectedDays.to))
      ) {
        setSelectedDays({ from: day, to: null });
      } else {
        setSelectedDays({ ...selectedDays, from: day });
      }
    },
    [setSelectedDays, selectedDays]
  );

  const getFlightTotalPrice = useCallback(
    (
      origin: string,
      destination: string,
      departure: Date
    ): { totalPrice: number; currency: string } => {
      const foundResponse = periodCalendarResults.find((result) => {
        return (
          result.requestParams.origin === origin &&
          result.requestParams.destination === destination &&
          result.response[moment(departure).format("YYYY-MM-DD")]
        );
      });

      if (foundResponse) {
        const flight =
          foundResponse.response[moment(departure).format("YYYY-MM-DD")][0];
        return {
          totalPrice: flight.price * passengerAmount,
          currency: flight.currency,
        };
      }
      return { totalPrice: 0, currency: "" };
    },
    [passengerAmount, periodCalendarResults]
  );

  const getSelectedFlightsTotalPrice = useCallback((): {
    totalPrice: number;
    currency: string;
  } => {
    const { totalPrice: departureTotalPrice, currency } = getFlightTotalPrice(
      query.origin,
      query.destination,
      selectedDays.from
    );
    const { totalPrice: returnTotalPrice } = getFlightTotalPrice(
      query.destination,
      query.origin,
      selectedDays.to
    );
    return { totalPrice: departureTotalPrice + returnTotalPrice, currency };
  }, [
    getFlightTotalPrice,
    query.destination,
    query.origin,
    selectedDays.from,
    selectedDays.to,
  ]);

  return {
    enabled: lowCostCalendarsPageEnabled,
    passengers,
    addPassenger,
    removePassenger,
    flightsLoaded,
    getFirstDayOfMonthOffset,
    getFlights,
    firstAvailableMonthOffset,
    selectDay,
    selectedDays,
    getSelectedFlightsTotalPrice,
  };
}
